import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import SshSecurityInformationForm from 'routes/SshSecurityInformation/components/SshSecurityInformationForm';
import { Ref } from 'routes/SshSecurityInformation/components/SshSecurityInformationForm/SshSecurityInformationForm';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as sshSecurityInfoSelectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as sshSecurityInfoActions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import { adaptToCreate } from 'data/sshSecurityInfo/sshSecurityInfo.adapter';
import { SshSecurityInfo } from 'data/sshSecurityInfo/sshSecurityInfo.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newSshSecurityInfo } from 'routes/SshSecurityInformation/SshSecurityInformation.types';
import { sshSecurityInfoTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptSshSecurityInfoTemplate,
} from '../../utils/sshSecurityInformationTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/sshSecurityInformationTemplateDataToSubmit';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  sshSecurityInfoTemplateDef: adaptTemplateVisibilityMode(
    sshSecurityInfoTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  sshSecurityInfoTemplate: adaptTemplateVisibilityMode(sshSecurityInfoTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  sshSecurityInfos: sshSecurityInfoSelectors.getSelectedSshSecurityInfos(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(
    state,
  ),
  enabledAccessFunction: supportedFeaturesSelectors.getEnabledAccessFunction(
    state,
  ),
});

const mapDispatch = {
  createSshSecurityInfo: sshSecurityInfoActions.createSshSecurityInfo,
  goToSshSecurityInfoList: sshSecurityInfoActions.goToSshSecurityInfoList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SshSecurityInformationCreate = ({
  history,
  userId,
  deliveryFunctionId,
  goToSshSecurityInfoList,
  createSshSecurityInfo,
  userName,
  isAuditEnabled,
  sshSecurityInfoTemplateDef,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<SshSecurityInfo>(
    cloneDeep(newSshSecurityInfo),
  );
  const [template, setTemplate] = useState(sshSecurityInfoTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SshSecurityInfo);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptSshSecurityInfoTemplate(
      clonedTemplate,
      fieldName,
      value,
      newData,
    );

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateSSHActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();
    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(
        distributorConfigFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateSSHActionPayload();
    if (payload) {
      createSshSecurityInfo(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToSshSecurityInfoList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <SshSecurityInformationForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="SshSecurityInformationCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('sshSecurityInfo.grid.newsshSecurityInfo'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SshSecurityInformationCreate));
